import { Autocomplete, Button, Popover, TextField } from '@mui/material';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { useDispatch, useSelector } from "react-redux";
import { LoadOrganizations, UpdateMenuFlag } from '../../actions';
import { exportToExcel } from '../../helpers/ExportToExcel';
import { setHeaderTitle, setRefreshHandler } from '../../helpers/header.helper';
import { BurgerMenuIcon, DateIcon, SecureFileBetaFeatureIcon, MultiSelectCrossIcon, DownloadWhiteIcon, NewDateIcon } from '../../icon';
import { CreditLogsModel, mapToCreditsLogs } from '../../models/credit.log.model';
import { OrganizationModel } from '../../models/organization.model';
import { ProfileModel } from '../../models/profile.model';
import * as selector from "../../selectors/selectors";
import { ReportService } from '../../services/ReportService';
import { UserService } from '../../services/UserService';
import { UtilityService } from '../../services/UtilityService';
import ButtonComponent from '../shared/button/button.component';
import { ActionLabels, ToasterTexts, buttonLabels, emptyLabels, pageTitles } from '../shared/constants';
import GridComponent from '../shared/grid';
import PageHeaderComponent from '../shared/page-header.component';

interface IProps { }

const CreditLogsComponent = (props: IProps) => {
    const dispatch = useDispatch()
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage()
    const Organizations: OrganizationModel[] = useSelector(selector.organizations)
    const [creditLogsData, setCreditLogsData] = useState<CreditLogsModel[]>([]);
    const [gridApi, setGridApi] = useState<any>(null);
    const [selectedOrg, setSelectedOrg] = useState<string>();
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [selectedMonth, setSelectedMonth] = useState<string>();
    const [ShowMultiActionButtons, setShowMultiActionButtons] = useState<boolean>(false)
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    useMemo(() => {
        if (Profile.RoleID === "1") {
            LoadOrganizations(dispatch);
        }
    }, [])

    const loadLogsData = (val) => {
        gridApi.showLoadingOverlay();
        if (!val) {
            gridApi.hideOverlay();
            gridApi.showNoRowsOverlay();
        } else {
            ReportService.getCreditLogs(selectedMonth, val).then(res => {
                setCreditLogsData(res.data.map(mapToCreditsLogs));
                gridApi.hideOverlay();
                gridApi.showNoRowsOverlay();
            }
            ).catch(err => {
                gridApi.hideOverlay();
                gridApi.showNoRowsOverlay();
            });
        }
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onGridReady = (params) => {
        setGridApi(params.api)
    }

    const onChange = (date: Date) => {
        setSelectedDate(date);
        setSelectedMonth(`${date.getFullYear()}-${date.getMonth() + 1}`);
        handleClose();
    }

    useEffect(() => {
        if (!!selectedMonth && !!selectedOrg) {
            setShowMultiActionButtons(true)
        }
        else {
            setShowMultiActionButtons(false)
        }
        if (UserService.hasSysAdminRole()) {
            setRefreshHandler(() => loadLogsData(selectedOrg));
        }
    }, [selectedOrg, selectedMonth])

    const clearSelection = () => {
        if (!!gridApi && !!creditLogsData.length)
            gridApi.deselectAll();
        if (!!selectedOrg.length) {
            setShowMultiActionButtons(false)
            setSelectedOrg("")
            setSelectedDate(undefined);
            setSelectedMonth(undefined);
        }
    }

    const ReportsColoumnDefs: ColDef[] = [
        {
            field: "PhoneNumberName",
            headerName: "Name",
            minWidth: 190,
            sort: !IsMobileDevice ? 'desc' : null,
            valueGetter: (params: ValueGetterParams) => {
                return UtilityService.trimMessage(params.data.PhoneNumberName, 25)
            },
        },
        {
            field: "PhoneNumber",
            minWidth: 160,
            headerName: "Phone Number",
        },
        {
            field: "NoramlCredits",
            headerName: "Normal 1-1 Credits",
            minWidth: 150,
            hide: IsMobileDevice,
            valueGetter: (params: ValueGetterParams) => {
                return params.data.NoramlCredits.toString()
            },
        },
        {
            field: "NoramlAddOnCredits",
            headerName: "Add-on 1-1 Credits",
            minWidth: 200,
            hide: IsMobileDevice
        },
        {
            field: "NoramlCreditsUsed",
            headerName: "Used 1-1 Credits",
            minWidth: 200,
            hide: IsMobileDevice
        },
        {
            field: "NoramlCreditsLeft",
            headerName: "Balance 1-1 Credits",
            minWidth: 200,
            valueGetter: (params: ValueGetterParams) => {
                return params.data.NoramlCreditsLeft.toString()
            },
        },
        {
            field: "GroupCredits",
            headerName: "Normal Group Credits",
            minWidth: 220
        },
        {
            field: "GroupAddOnCredits",
            headerName: "Add-on Group Credits",
            minWidth: 220
        },
        {
            field: "GroupCreditsUsed",
            headerName: "Used Group Credits",
            minWidth: 220
        },
        {
            field: "GroupCreditsLeft",
            headerName: "Balance Group Credits",
            minWidth: 220
        }
    ]

    useEffect(() => {
        setHeaderTitle(pageTitles.CreditLog);
        // setRefreshHandler(gridRefresh);
        return setHeaderTitle
    }, [])

    const ExportReportHeaders = [
        { label: "Name", value: "PhoneNumberName" },
        { label: "Phone Number", value: "PhoneNumber" },
        { label: "Normal 1-1 Credits", value: "NoramlCredits" },
        { label: "Add-on 1-1 Credits", value: "NoramlAddOnCredits" },
        { label: "Used 1-1 Credits", value: "NoramlCreditsUsed" },
        { label: "Balance 1-1 Credits", value: "NoramlCreditsLeft" },
        { label: "Normal Group Credits", value: "GroupCredits" },
        { label: "Add-on Group Credits", value: "GroupAddOnCredits" },
        { label: "Used Group Credits", value: "GroupCreditsUsed" },
        { label: "Balance Group Credits", value: "GroupCreditsLeft" }
    ]

    const minDate = new Date(2023, 10, 1);
    const maxDate = new Date()

    const searchReport = (e: any) => {
        gridApi.setQuickFilter(e.target.value);
    }

    return <div className='h-p-100'>
        {IsMobileDevice &&
            <div className="d-flex">
                <span className={`ml-1 my-1 h1`}>
                    <BurgerMenuIcon className="mr-1" onClick={(e) => { e.stopPropagation(); UpdateMenuFlag(dispatch, true) }} style={{ marginTop: "0.25rem" }} />
                </span>
                <span className="my-auto h1">
                    Credit Logs
                </span>
                <SecureFileBetaFeatureIcon className={"p-absolute l-18 t-2"} />
            </div>
        }
        <div className='d-flex h-p-100 flex-direction mx-2 mb-1 mt-1'>
            <div className={`d-flex ${IsMobileDevice ? "flex-direction" : "flex-direction-row"} justify-between mb-1`}>
                <div className='d-flex flex-direction w-p-100'>
                    <div className="report-text color-conv-section-inactive ml-1" >
                        <div>View monthly credit logs to track the number of credits assigned and used. Use the search filter to select and analyze data by month for better credit management.</div>
                    </div>
                    <div className='d-flex justify-between' >
                        <div className='d-flex flex-direction-row'>
                            <div className={` ${IsMobileDevice ? "w-p-60" : ""}`}>
                                <div className={`${IsMobileDevice ? "w-p-100" : ""} d-flex flex-direction-row align-center`}>
                                    
                                    <Button className={`mt-2 bg-color-message-log-bg`} aria-describedby={id} onClick={handleClick}>
                                        <NewDateIcon className='mx-1' />
                                        <span style={{ color: "#3E5172", fontSize: "11px", textTransform: "capitalize",marginRight:"8px" }}>
                                            {selectedDate ? `${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}` : "Select Month"}</span>
                                    </Button >
                                </div>
                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    className={`mt-1 mr-1`}

                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                >
                                    <Calendar className="px-1 pb-1 br-1" minDate={minDate} maxDate={maxDate} value={selectedDate} onClickMonth={onChange} maxDetail="year" />
                                </Popover>

                            </div>
                            {!UserService.hasSysAdminRole() &&
                                <ButtonComponent
                                    ClassName={`c-pointer p-relative justify-center px-2 p-0 mt-2`}
                                    OnClick={() => { loadLogsData(UserService.hasSysAdminRole() ? selectedOrg : UserService.getCurrentUserOrgId()) }} ButtonLabel={'Search'}
                                    Variant={selectedDate ? 'primary-btn-small' : 'disabled-btn-small'}
                                />}
                            <div>
                                {UserService.hasSysAdminRole() &&
                                    <Autocomplete
                                        size="small"
                                        className={"w-v-15 mt-2 w-fill"}
                                        options={Organizations}
                                        getOptionLabel={(option: any) => option.Name ? option.Name : ""}
                                        onChange={(e: any, value: any) => {
                                            setSelectedOrg(!!value?.ID ? value.ID : "");
                                        }}
                                        value={Organizations.find(x => x.ID === selectedOrg)
                                            || ""
                                        }
                                        renderOption={(defaultprops, option: any) => (
                                            <li {...defaultprops} className={" c-pointer h6-regular d-flex p-1 justify-between w-p-80"}>
                                                {option?.Name}
                                            </li>
                                        )}
                                        classes={{
                                            input: "color-text-field-font h6-regular"
                                        }}
                                        tabIndex={16}
                                        renderInput={(params) => <TextField {...params} name="Select Organizations *" size="small" variant="outlined" label="Select Organizations *" placeholder="Select Organizations *" />}
                                    />}
                            </div>
                            <div className={'mt-2 mb-1'}>
                                {!IsMobileDevice &&
                                    <PageHeaderComponent
                                        onSearch={searchReport}
                                        PlaceHolder={"Search..."}
                                        ShowMultiActionButtons={ShowMultiActionButtons}
                                        MultiActionButtons={[
                                            {
                                                Name: buttonLabels.APPLY,
                                                Variant: "primary",
                                                ClassName: "",
                                                Hide: !UserService.hasSysAdminRole(),
                                                OnClick: () => { loadLogsData(selectedOrg) }
                                            },
                                            {
                                                Name: ActionLabels.CLEAR_SELECTION,
                                                Variant: "text",
                                                ClassName: "p-0 mr-1 w-space",
                                                Icon: MultiSelectCrossIcon,
                                                Hide: false,
                                                OnClick: () => clearSelection()
                                            }
                                        ]}
                                    />
                                }
                            </div>
                        </div>
                        <div className='d-flex'>
                            {creditLogsData.length > 0 &&
                                <ButtonComponent
                                    Variant={"primary-btn-small"}
                                    ClassName={`h5-regular c-pointer d-flex justify-flex-end ${IsMobileDevice ? "h-v-4 mt-2" : "h-v-5 mt-1"}`}
                                    OnClick={() => {
                                        if (creditLogsData.length > 0) {
                                            exportToExcel(ExportReportHeaders, creditLogsData, `FalkonSMS-Credit-Los-Report-${moment(UtilityService.getPreferredTimeZoneTimeNow(), ["MM-DD-YYYY"]
                                            ).format("MM-DD-YYYY")}`, ToasterTexts.EXCEL_EXPORT_REPORT_MESSAGE);
                                        }
                                    }}
                                    Icon={DownloadWhiteIcon}
                                    ButtonLabel={buttonLabels.DOWNLOAD}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>
            {<div className={`${!IsMobileDevice ? "h-p-100" : "h-p-70"} o-hidden p-relative mb-1`}>
                {<GridComponent
                    ColumnDefs={ReportsColoumnDefs}
                    RowData={creditLogsData}
                    OnGridReady={onGridReady}
                    IsPaginationEnabled={!IsMobileDevice}
                    EmptyLabel={emptyLabels.CREDITS}
                    gridApi={gridApi}
                    LockVisible={false}
                    isHorizontalScroll={true} />}
            </div>}
        </div>
    </div>
}
export default CreditLogsComponent